import { useCallback, useState } from "react";
import { AuthApi } from "../../infrastructure/api/AuthApi";
import { EnableTotpRepositoryImpl } from "../../infrastructure/repositories/EnableTotpRepositoryImpl";
import type { QrCode } from "../../domain/entities/QrCode";
import { requireAccessToken } from "./useStepUpVerification";

/**
 * Sets up the authenticator app for a signed-in user (QR, then confirm a
 * code), and resets an existing one before reconfiguring.
 */
export const useAuthenticatorSetup = () => {
  const [loading, setLoading] = useState(false);

  const run = useCallback(async <T,>(action: () => Promise<T>): Promise<T> => {
    setLoading(true);
    try {
      return await action();
    } finally {
      setLoading(false);
    }
  }, []);

  const startAuthenticatorSetup = useCallback(
    (): Promise<QrCode> =>
      run(() => new AuthApi().getQrCode(requireAccessToken())),
    [run],
  );

  const confirmAuthenticatorSetup = useCallback(
    (code: string) =>
      run(async () => {
        const result = await new EnableTotpRepositoryImpl().verifyTotpSetup(
          requireAccessToken(),
          code,
        );
        if (result.success === false) {
          throw new Error("Invalid code. Please try again.");
        }
        sessionStorage.setItem("is_totp_enabled", "true");
      }),
    [run],
  );

  /** Needs the RESET_TOTP temp token from verify-otp (email code only). */
  const resetAuthenticator = useCallback(
    (tempAccessToken: string) =>
      run(async () => {
        await new EnableTotpRepositoryImpl().resetTotp(tempAccessToken);
        sessionStorage.setItem("is_totp_enabled", "false");
      }),
    [run],
  );

  return {
    startAuthenticatorSetup,
    confirmAuthenticatorSetup,
    resetAuthenticator,
    loading,
  };
};
